type ApiErrorLike = {
  statusCode?: number;
  status?: number;
  code?: number | string;
  message?: string | string[];
  details?: Record<string, unknown>;
};

const NETWORK_ERROR_MESSAGE =
  "No internet connection. Check your connection and try again.";

const NETWORK_ERROR_PATTERNS = [
  "failed to fetch",
  "load failed",
  "networkerror",
  "network request failed",
] as const;

const normalizeMessage = (value: unknown): string | undefined => {
  if (Array.isArray(value)) {
    const message = value
      .filter((item): item is string => typeof item === "string")
      .join("\n");
    return message || undefined;
  }

  return typeof value === "string" && value.trim() ? value : undefined;
};

export const getHttpStatus = (e: unknown): number | undefined => {
  if (!e || typeof e !== "object") return undefined;
  const err = e as ApiErrorLike;
  return (
    err.statusCode ??
    err.status ??
    (typeof err.code === "number" ? err.code : undefined)
  );
};

export const getErrorMessage = (e: unknown): string | undefined => {
  if (!e || typeof e !== "object") return undefined;
  const err = e as ApiErrorLike;

  return (
    normalizeMessage(err.details?.message) ?? normalizeMessage(err.message)
  );
};

export const isNetworkError = (e: unknown): boolean => {
  const status = getHttpStatus(e);
  if (status && status !== 0) return false;

  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return true;
  }

  const message = getErrorMessage(e)?.toLowerCase() ?? "";
  return NETWORK_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
};

export const getUserFriendlyErrorMessage = (
  e: unknown,
  fallback = "Something went wrong. Please try again.",
): string => {
  if (isNetworkError(e)) {
    return NETWORK_ERROR_MESSAGE;
  }

  return getErrorMessage(e) ?? fallback;
};

export const is404 = (e: unknown) => getHttpStatus(e) === 404;
